import React from "react";
import { FormGroup, Label } from "reactstrap";
import classNames from "classnames";

import { Icon } from "../../../shared/Icon/Icon";
import { Input } from "../../../shared/Input/Input";
import { FormSubSection } from "../../../shared/FormSubSection/FormSubSection";
import { CalendarProvider } from "../CalendarProvider/CalendarProvider";
import { CrossOrgCalendarEmail } from "./CrossOrgCalendarEmail";
import { Providers } from "../../../../lib/context/Calendar/CalendarContextProvider";
import { RoomFormik } from "../../../shared/Forms/RoomForm/lib/useRoomFormik";
import { LabelDirectionType } from "../../../shared/LabelWithInformationBox/LabelWithInformationBox";
import { CrossOrganizationIntegration } from "../../../../api/graphql/rooms/rooms";
import { CalendarType } from "../../../../api/grpc/calendarprovider/calendarprovider";
import { Tag } from "../../../shared/Tag/Tag";
import { InformationBox } from "../../../shared/InformationBox/InformationBox";
import { InputDescription } from "../../../shared/InputDescription/InputDescription";
import { tooltips } from "../../../../lib/utils/tooltips";
import { useToggleModal } from "../../../../lib/hooks/useToggleModal";
import { ContactSalesModal } from "../../../ContactSales/ContactSalesModal";

import "./CrossOrgCalendarSection.scss";

interface Props {
  formik: RoomFormik;
  providers: Providers[];
  direction?: LabelDirectionType;
  roomId?: string;
  hasCrossOrgCalendarEntitlement: boolean;
  edit?: boolean;
  canShowCrossOrgSection?: boolean;
  canEditCrossOrgSection?: boolean;
  canAddSecondCrossOrg?: boolean;
}

export const CrossOrgCalendarSection = (props: Props) => {
  const {
    formik,
    providers,
    direction,
    roomId,
    hasCrossOrgCalendarEntitlement,
    edit,
    canShowCrossOrgSection = false,
    canEditCrossOrgSection = false,
    canAddSecondCrossOrg = false,
  } = props;
  const { isOpen, toggleModal } = useToggleModal();
  const isEwsMainCalendar = formik.values.calendarType === CalendarType.EWS;
  const crossOrgProviders = providers.filter(
    (provider) =>
      !isEwsMainCalendar &&
      (provider.calType === CalendarType.GSUITE ||
        provider.calType === CalendarType.O365)
  );

  // Only show in edit mode
  if (!edit) {
    return null;
  }

  const crossOrganizationIntegration =
    formik.values.crossOrganizationIntegration || [];
  const crossOrgCalendarsEnabled = crossOrganizationIntegration.length > 0;

  const handleAddCrossOrgCalendar = () => {
    const newCalendar: CrossOrganizationIntegration = {
      roomId: roomId || "",
      calendarProviderId: "",
      calendarId: "",
      order: crossOrganizationIntegration.length + 1,
    };
    formik.setFieldValue("crossOrganizationIntegration", [
      ...crossOrganizationIntegration,
      newCalendar,
    ]);
  };

  const handleRemoveCrossOrgCalendar = (index: number) => {
    // Remove this calendar and all calendars after it (cascading)
    const newCalendars = crossOrganizationIntegration.slice(0, index);
    formik.setFieldValue("crossOrganizationIntegration", newCalendars);
  };

  const handleProviderChange = (
    index: number,
    providerId: string,
    calType: CalendarType
  ) => {
    const newCalendars = [...crossOrganizationIntegration];
    newCalendars[index] = {
      ...newCalendars[index],
      roomId: roomId || "",
      calendarProviderId: providerId,
      calendarId: "",
      order: index + 1,
    };
    formik.setFieldValue("crossOrganizationIntegration", newCalendars);
  };

  const handleProviderRemove = (index: number) => {
    handleRemoveCrossOrgCalendar(index);
  };

  const hasMainCalendar =
    !!formik.values.calendarProviderId && !!formik.values.calendarId;
  const hasFirstCrossOrgReady =
    !!crossOrganizationIntegration[0]?.calendarProviderId &&
    !!crossOrganizationIntegration[0]?.calendarId;
  const canEditFirstCrossOrg =
    hasCrossOrgCalendarEntitlement && canEditCrossOrgSection;
  const canEditSecondCrossOrg =
    hasCrossOrgCalendarEntitlement && canAddSecondCrossOrg;
  const showSaveToUnlock =
    hasCrossOrgCalendarEntitlement &&
    (!canEditFirstCrossOrg ||
      (hasFirstCrossOrgReady && !canEditSecondCrossOrg));

  // Only show once a main calendar is selected in the form.
  if (!canShowCrossOrgSection || !hasMainCalendar) {
    return null;
  }

  return (
    <div className="CrossOrgCalendarSection">
      <div className="CrossOrgCalendarSection__header">
        <Icon icon="arrow-merge" className="CrossOrgCalendarSection__icon" />
        <span className="CrossOrgCalendarSection__title">
          Cross-organization shared room
        </span>
        {!hasCrossOrgCalendarEntitlement && (
          <Tag
            icon="lock"
            title="BusinessPLUS Add-on"
            information={tooltips.rooms.settings.crossOrgCalendarAddon}
          />
        )}
      </div>

      <p className="CrossOrgCalendarSection__description">
        Merge multiple room calendars into a single room, allowing users to book
        it as if it were part of their own Microsoft or Google tenant. EWS is
        not supported{" "}
        {!hasCrossOrgCalendarEntitlement && (
          <>
            <br></br>
            <strong>Important!</strong> This feature requires the
            Cross-Organization Shared Room Calendars add-on. Contact Goget Sales{" "}
            <button
              type="button"
              className="CrossOrgCalendarSection__contactSalesButton"
              onClick={toggleModal}
            >
              here
            </button>
            .
          </>
        )}
      </p>

      <FormGroup
        className={classNames("flex-a-center", {
          disabled: !canEditFirstCrossOrg,
        })}
      >
        <Input
          type="checkbox"
          id="crossOrgCalendarsEnabled"
          name="crossOrgCalendarsEnabled"
          checked={crossOrgCalendarsEnabled}
          disabled={!canEditFirstCrossOrg}
          onChange={(e) => {
            if (!canEditFirstCrossOrg) {
              return;
            }
            const value = e.target.checked;
            if (!value) {
              // If unchecking, clear all cross-org calendars
              formik.setFieldValue("crossOrganizationIntegration", []);
            } else if (value && crossOrganizationIntegration.length === 0) {
              // If checking and no calendars exist, add the first one
              handleAddCrossOrgCalendar();
            }
          }}
        />
        <Label htmlFor="crossOrgCalendarsEnabled" className="checkbox_label">
          <span>Add first cross-org calendar</span>
          <span className="checkbox_box"></span>
        </Label>
        <InformationBox
          message={tooltips.rooms.settings.crossOrgCalendar}
          className="ml-2"
        />
      </FormGroup>

      {crossOrgCalendarsEnabled && crossOrganizationIntegration.length > 0 && (
        <div className="CrossOrgCalendarSection__calendars">
          <FormSubSection
            column
            title="Cross-org calendar 1"
            className="CrossOrgCalendarSection__calendar"
          >
            <CalendarProvider
              providers={crossOrgProviders}
              value={crossOrganizationIntegration[0]?.calendarProviderId}
              direction={direction}
              label="Select cross-org calendar integration"
              disabled={!canEditFirstCrossOrg}
              onChange={(providerId, calType) =>
                handleProviderChange(0, providerId, calType)
              }
              onRemove={() => handleProviderRemove(0)}
            />
            {crossOrgProviders.some(
              (provider) =>
                provider.iD ===
                crossOrganizationIntegration[0]?.calendarProviderId
            ) && (
              <CrossOrgCalendarEmail
                formik={formik}
                providers={crossOrgProviders}
                roomId={roomId}
                index={0}
                label="Select cross-org room calendar"
                disabled={!canEditFirstCrossOrg}
              />
            )}
          </FormSubSection>

          {hasFirstCrossOrgReady && (
            <FormGroup
              className={classNames("flex-a-center", {
                disabled: !canEditSecondCrossOrg,
              })}
            >
              <Input
                type="checkbox"
                id="addSecondCrossOrgCalendar"
                name="addSecondCrossOrgCalendar"
                checked={crossOrganizationIntegration.length >= 2}
                disabled={
                  !canEditSecondCrossOrg ||
                  !crossOrganizationIntegration[0]?.calendarId
                }
                onChange={(e) => {
                  if (!canEditSecondCrossOrg) {
                    return;
                  }
                  if (e.target.checked) {
                    handleAddCrossOrgCalendar();
                  } else {
                    handleRemoveCrossOrgCalendar(1);
                  }
                }}
              />
              <Label
                htmlFor="addSecondCrossOrgCalendar"
                className="checkbox_label"
              >
                <span>Add second cross-org calendar</span>
                <span className="checkbox_box"></span>
              </Label>
              <InformationBox
                message={tooltips.rooms.settings.crossOrgCalendar}
                className="ml-2"
              />
            </FormGroup>
          )}

          {crossOrganizationIntegration.length >= 2 && (
            <FormSubSection
              column
              title="Cross-org calendar 2"
              className="CrossOrgCalendarSection__calendar"
            >
              <CalendarProvider
                providers={crossOrgProviders}
                value={crossOrganizationIntegration[1]?.calendarProviderId}
                direction={direction}
                label="Select cross-org calendar integration"
                disabled={!canEditSecondCrossOrg}
                onChange={(providerId, calType) =>
                  handleProviderChange(1, providerId, calType)
                }
                onRemove={() => handleProviderRemove(1)}
              />
              {crossOrgProviders.some(
                (provider) =>
                  provider.iD ===
                  crossOrganizationIntegration[1]?.calendarProviderId
              ) && (
                <CrossOrgCalendarEmail
                  formik={formik}
                  providers={crossOrgProviders}
                  roomId={roomId}
                  index={1}
                  label="Select cross-org room calendar"
                  disabled={!canEditSecondCrossOrg}
                />
              )}
            </FormSubSection>
          )}

          <FormGroup
            className={classNames("flex-a-center", {
              disabled: !canEditFirstCrossOrg,
            })}
          >
            <Input
              type="checkbox"
              id="privateCrossOrganizationEvent"
              name="privateCrossOrganizationEvent"
              checked={formik.values.privateCrossOrganizationEvent}
              disabled={!canEditFirstCrossOrg}
              onChange={formik.handleChange}
            />
            <Label
              htmlFor="privateCrossOrganizationEvent"
              className="checkbox_label"
            >
              <span>Keep cross-tenant events private</span>
              <span className="checkbox_box"></span>
            </Label>
            <InformationBox
              message={tooltips.rooms.settings.privateCrossOrganizationEvent}
              className="ml-2"
            />
          </FormGroup>
        </div>
      )}
      {showSaveToUnlock && (
        <InputDescription
          title="Save room settings to unlock cross-organization shared room."
          className="custom-font mt-3"
        />
      )}
      {isOpen && <ContactSalesModal isOpen={isOpen} toggle={toggleModal} />}
    </div>
  );
};
